import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useCallback, useMemo } from 'react'
import { db, ScreenplayEntity } from '@/shared/lib/db'
import { Character } from '@storyverse/shared'
import { $getRoot } from 'lexical'
import { SuggestionListItem } from '../type'
import { NodeType } from './nodes/NodeType'
import { SceneNode } from './nodes/SceneNode'

import { useCharactersStore } from '@/features/shared/stores/characters.store'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { useScreenplayEditor } from '../../hooks/useScreenplayEditor'
import { useSpEditorStore } from '../../stores/sp-editor.store'
import { PageBreakNode } from './nodes/PageBreakNode'
import { TSuggestionProps } from './plugins/SpSuggestionPlugin'
import { getCurrentNode } from './utils/NodeUtils'
import { characterService } from '@/features/characters/service/character.service'
import { useSaveCharacterMutation, useCharacters } from '@/features/characters/queries/character.queries'

type SpEditorContextType = {
    characters: Character[]
    addCharacter: (character: Character) => void
    featuresEnabled: FeatureEnabled
    updateFeatureEnabled: (feature: FeatureEnabled) => void
    suggestionProps: TSuggestionProps<SuggestionListItem>
    setSuggestionProps: Dispatch<SetStateAction<TSuggestionProps<SuggestionListItem>>>
    htmlString: string
    setHtmlString: (htmlString: string) => void
    screenplayEntity: ScreenplayEntity | undefined
    setScreenplayEntity: Dispatch<SetStateAction<ScreenplayEntity | undefined>>
    project: any
}
type FeatureEnabled = {
    aiCompletion: boolean
    nodeToolbar: boolean
    suggestionDropdown: boolean
}

export const SpEditorContext = createContext<SpEditorContextType | null>(null)

export function SpEditorProvider({ children }: { children: React.ReactNode }) {
    const { project } = useWorkspace()
    const store = useSpEditorStore()
    const { setScenes } = useSceneStore()
    const { characters } = useCharactersStore()
    const saveCharacterMutation = useSaveCharacterMutation()

    // Fetch and sync characters
    useCharacters(project?.id || '')

    // Initialize editor logic (loads characters, features)
    useScreenplayEditor()

    const { editor } = useSceneStore()

    // Mutation listeners
    useEffect(() => {
        if (!editor) return

        return editor.registerMutationListener(
            SceneNode,
            (mutatedNodes, { updateTags, dirtyLeaves, prevEditorState }) => {
                const hasCreatedOrDestroyed = () => {
                    for (const val of mutatedNodes.values()) {
                        if (val === 'created' || val === 'destroyed') return true
                    }
                    return false
                }
                editor?.getEditorState().read(() => {

                    const sceneNodes = $getRoot().getChildren().filter((e) => {
                        return e instanceof SceneNode
                    })
                    setScenes(sceneNodes)

                })
                if (hasCreatedOrDestroyed()) {
                    editor.update(() => {
                        const currentNode = getCurrentNode()
                        currentNode?.getNextSiblings()?.forEach((node) => {
                            if (node.__type === NodeType.Scene) {
                                ; (node as SceneNode).markDirty()
                            }
                        })
                    })
                }
            }
        )
    }, [editor, setScenes])

    useEffect(() => {
        if (!editor) return

        return editor.registerMutationListener(
            PageBreakNode,
            (mutatedNodes, { updateTags, dirtyLeaves, prevEditorState }) => {
                const hasCreatedOrDestroyed = () => {
                    for (const val of mutatedNodes.values()) {
                        if (val === 'created' || val === 'destroyed') return true
                    }
                    return false
                }

                if (hasCreatedOrDestroyed()) {
                    editor.update(() => {
                        const currentNode = getCurrentNode()
                        currentNode?.getNextSiblings()?.forEach((node) => {
                            if (node.__type === NodeType.PageBreak) {
                                ; (node as PageBreakNode).markDirty()
                            }
                        })
                    })
                }
            }
        )
    }, [editor])

    // Compatibility layer: Map store functions to context shape
    const addCharacter = useCallback((c: Character) => {
        if (!project?.id) return
        const character = characterService.buildCharacter({
            ...c,
            projectId: project.id,
        })
        saveCharacterMutation.mutate(character)
    }, [project?.id, saveCharacterMutation])

    const updateFeatureEnabled = useCallback((f: FeatureEnabled) => {
        store.updateFeatureEnabled(f)
    }, [store.updateFeatureEnabled])

    const setSuggestionProps = useCallback((val: SetStateAction<TSuggestionProps<SuggestionListItem>>) => {
        if (typeof val === 'function') {
            const newVal = val(store.suggestionProps)
            store.setSuggestionProps(newVal)
        } else {
            store.setSuggestionProps(val)
        }
    }, [store.suggestionProps, store.setSuggestionProps])

    const setHtmlString = useCallback((s: string) => {
        store.setHtmlString(s)
    }, [store.setHtmlString])

    const setScreenplayEntity = useCallback((val: SetStateAction<ScreenplayEntity | undefined>) => {
        if (typeof val === 'function') {
            const newVal = val(store.screenplayEntity)
            store.setScreenplayEntity(newVal)
        } else {
            store.setScreenplayEntity(val)
        }
    }, [store.screenplayEntity, store.setScreenplayEntity])

    const contextValue = useMemo(() => ({
        characters: characters as any,
        addCharacter,
        featuresEnabled: store.featuresEnabled,
        updateFeatureEnabled,
        suggestionProps: store.suggestionProps,
        setSuggestionProps,
        htmlString: store.htmlString,
        setHtmlString,
        screenplayEntity: store.screenplayEntity,
        setScreenplayEntity,
        project
    }), [
        characters,
        addCharacter,
        store.featuresEnabled,
        updateFeatureEnabled,
        store.suggestionProps,
        setSuggestionProps,
        store.htmlString,
        setHtmlString,
        store.screenplayEntity,
        setScreenplayEntity,
        project
    ])

    return (
        <SpEditorContext.Provider value={contextValue}>
            {children}
        </SpEditorContext.Provider>
    )
}

export function useSpEditorProvider() {
    const context = useContext(SpEditorContext)
    if (!context)
        throw new Error('useSpEditorProvider must be used within a ScreenplayEditorProvider')
    return context
}
